import { classifySentence, splitIntoSentences } from './classification';

interface DriverData {
  contextOfPurpose: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
}

interface ValidationResults {
  diagnostics: string[];
  suggestions: string[];
  classificationIssues?: {
    currentState: string[];
    problemOpportunity: string[];
    ramifications: string[];
  };
}

export function validateDriver(driver: DriverData): ValidationResults {
  const diagnostics: string[] = [];
  const suggestions: string[] = [];
  const classificationIssues = {
    currentState: [] as string[],
    problemOpportunity: [] as string[],
    ramifications: [] as string[]
  };

  // Check for empty required fields
  if (!driver.currentState.trim()) {
    diagnostics.push("You haven't described the current state yet. Start with what's actually happening.");
  }

  if (!driver.problemOpportunity.trim()) {
    diagnostics.push("You haven't described the problem or opportunity yet. What's the core issue?");
  }

  if (!driver.ramifications.trim()) {
    diagnostics.push("We don't yet see a clear statement of why this matters or what's at stake if nothing changes. Add at least one sentence making that explicit.");
  }

  // Check if narrative is empty
  if (!driver.narrative.trim()) {
    diagnostics.push("Your Outcome Driver narrative is empty. Combine your three components into a flowing narrative.");
  }

  // Check for bullet list patterns
  const bulletPatterns = /^[\s]*[-*•]\s|^\d+\.\s/m;
  if (bulletPatterns.test(driver.narrative)) {
    diagnostics.push("Your driver is formatted as a list. Turn this into a short narrative so the logic flows clearly.");
    suggestions.push("Rewrite your driver as a paragraph or two, connecting the current state, problem, and ramifications with flowing sentences.");
  }

  // Check for plan/solution language
  const planPatterns = /^(we|i|our|my)\s+(need to|will|should|must|plan to|going to)/i;
  if (planPatterns.test(driver.narrative)) {
    diagnostics.push("This reads more like a solution or plan. Your outcome driver should focus on why change is needed, not how you'll execute it.");
    suggestions.push("Remove action-oriented language like 'we need to' or 'we will'. Focus on describing the current state, the problem, and why it matters.");
  }

  // Check for value judgment in ramifications
  const valueJudgmentPatterns = /(this is|this means|this would lead to|if (i|we) don't|because|matters because|stake|consequence|impact)/i;
  if (driver.ramifications.trim() && !valueJudgmentPatterns.test(driver.ramifications)) {
    suggestions.push("Your ramifications section could be stronger. Try explicitly stating why this matters using phrases like 'This is bad because...' or 'This matters because...'");
  }

  // Check if problem just restates current state
  if (driver.currentState.trim() && driver.problemOpportunity.trim()) {
    const currentStateWords = driver.currentState.toLowerCase().split(/\s+/).filter(w => w.length > 4);
    const problemWords = driver.problemOpportunity.toLowerCase().split(/\s+/).filter(w => w.length > 4);
    const overlap = currentStateWords.filter(w => problemWords.includes(w)).length;
    if (overlap > currentStateWords.length * 0.7) {
      suggestions.push("Your problem/opportunity section may be too similar to your current state. Focus on the gap, tension, or opening, not just restating the current situation.");
    }
  }

  // Check for narrative form (should be paragraph, not list)
  if (driver.narrative.trim()) {
    const sentences = driver.narrative.split(/[.!?]+/).filter(s => s.trim().length > 0);
    if (sentences.length < 2) {
      suggestions.push("Your narrative is quite short. Consider expanding it to connect the current state, problem, and ramifications more fully.");
    }
  }

  // Check alignment with context of purpose (basic keyword check)
  if (driver.contextOfPurpose.trim() && driver.narrative.trim()) {
    const contextWords = driver.contextOfPurpose.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    const narrativeWords = driver.narrative.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    const contextMatches = contextWords.filter(w => narrativeWords.includes(w)).length;
    if (contextMatches === 0 && contextWords.length > 0) {
      suggestions.push("Your driver doesn't seem to reference key terms from your context of purpose. Consider how the driver relates to the stated context.");
    }
  }

  // Enhanced validation using classification rules
  validateWithClassification(driver, suggestions, classificationIssues);

  // Jedi Check: fact → meaning → consequence structure
  const jediCheck = validateJediStructure(driver);
  if (!jediCheck.isValid) {
    diagnostics.push(jediCheck.message);
    suggestions.push(jediCheck.suggestion);
  }

  return { 
    diagnostics, 
    suggestions,
    classificationIssues: Object.keys(classificationIssues).some(k => classificationIssues[k as keyof typeof classificationIssues].length > 0) 
      ? classificationIssues 
      : undefined
  };
}

/**
 * Validate each section using classification rules
 */
function validateWithClassification(
  driver: DriverData,
  suggestions: string[],
  classificationIssues: { currentState: string[]; problemOpportunity: string[]; ramifications: string[] }
) {
  // Validate Current State - should contain facts, not interpretations
  if (driver.currentState.trim()) {
    const sentences = splitIntoSentences(driver.currentState);
    sentences.forEach(sentence => {
      const classification = classifySentence(sentence);
      if (classification.bucket !== 'current-state' && classification.confidence !== 'low') {
        if (classification.bucket === 'problem-opportunity') {
          classificationIssues.currentState.push(`"${sentence.substring(0, 50)}..." appears to be an interpretation, not a fact.`);
          suggestions.push('Current State should contain only observable facts. Move interpretations to the Problem/Opportunity section.');
        } else if (classification.bucket === 'ramifications') {
          classificationIssues.currentState.push(`"${sentence.substring(0, 50)}..." describes consequences, not current facts.`);
          suggestions.push('Current State should describe what is, not what will happen. Move consequences to the Ramifications section.');
        }
      }
    });
  }

  // Validate Problem/Opportunity - should contain meaning, not just restated facts
  if (driver.problemOpportunity.trim()) {
    const sentences = splitIntoSentences(driver.problemOpportunity);
    sentences.forEach(sentence => {
      const classification = classifySentence(sentence);
      if (classification.bucket === 'current-state' && classification.confidence === 'high') {
        classificationIssues.problemOpportunity.push(`"${sentence.substring(0, 50)}..." is a fact, not an interpretation of the facts.`);
        suggestions.push('Problem/Opportunity should explain what the facts mean, not just restate them. Focus on the core issue or tension.');
      } else if (classification.bucket === 'ramifications' && classification.confidence === 'high') {
        classificationIssues.problemOpportunity.push(`"${sentence.substring(0, 50)}..." describes consequences, not the problem itself.`);
        suggestions.push('Problem/Opportunity should describe the issue, not its consequences. Move consequences to the Ramifications section.');
      }
    });
  }

  // Validate Ramifications - should contain consequences, not just tension
  if (driver.ramifications.trim()) {
    const sentences = splitIntoSentences(driver.ramifications);
    sentences.forEach(sentence => {
      const classification = classifySentence(sentence);
      if (classification.bucket === 'problem-opportunity' && classification.confidence === 'high') {
        classificationIssues.ramifications.push(`"${sentence.substring(0, 50)}..." describes tension, not consequences.`);
        suggestions.push('Ramifications should describe what happens if nothing changes, not just the nature of the problem. Focus on stakes and consequences.');
      } else if (classification.bucket === 'current-state' && classification.confidence === 'high') {
        classificationIssues.ramifications.push(`"${sentence.substring(0, 50)}..." is a fact, not a consequence.`);
        suggestions.push('Ramifications should describe future consequences, not current facts.');
      }
    });
  }
}

/**
 * Jedi Check: Validate fact → meaning → consequence structure
 */
function validateJediStructure(driver: DriverData): { isValid: boolean; message: string; suggestion: string } {
  const hasCurrentState = driver.currentState.trim().length > 0;
  const hasProblem = driver.problemOpportunity.trim().length > 0;
  const hasRamifications = driver.ramifications.trim().length > 0;

  if (!hasCurrentState || !hasProblem || !hasRamifications) {
    return {
      isValid: false,
      message: 'The Outcome Driver structure is incomplete. Ensure all three sections are present.',
      suggestion: 'Complete all three sections: Current State (facts), Problem/Opportunity (meaning), and Ramifications (consequences).'
    };
  }

  // Check if the structure flows: fact → meaning → consequence
  const currentStateClassification = classifySentence(driver.currentState);
  const problemClassification = classifySentence(driver.problemOpportunity);
  const ramificationsClassification = classifySentence(driver.ramifications);

  if (currentStateClassification.bucket !== 'current-state' && currentStateClassification.confidence !== 'low') {
    return {
      isValid: false,
      message: 'Current State section does not clearly contain facts. The structure should start with observable facts.',
      suggestion: 'Ensure Current State contains only factual, observable statements. Use the Sentence Classifier if unsure.'
    };
  }

  if (problemClassification.bucket !== 'problem-opportunity' && problemClassification.confidence !== 'low') {
    return {
      isValid: false,
      message: 'Problem/Opportunity section does not clearly contain meaning. The structure should move from facts to interpretation.',
      suggestion: 'Ensure Problem/Opportunity explains what the facts mean, not just restates them. Use the Sentence Classifier if unsure.'
    };
  }

  if (ramificationsClassification.bucket !== 'ramifications' && ramificationsClassification.confidence !== 'low') {
    return {
      isValid: false,
      message: 'Ramifications section does not clearly contain consequences. The structure should end with stakes and consequences.',
      suggestion: 'Ensure Ramifications describes what happens if nothing changes. Use the Sentence Classifier if unsure.'
    };
  }

  return { isValid: true, message: '', suggestion: '' };
}


